import { OptimizedLocalStorage } from '@src/modules/optimization';
import type { RootState } from '@src/store/index';

// 最適化されたローカルストレージインスタンス
const optimizedStorage = OptimizedLocalStorage.getInstance();

export function storeReduxStateToLocalStorage(state: RootState) {
  optimizedStorage.set('app', state, 100); // 100msデバウンス

  if (typeof state.focusMode?.isFocusMode === 'boolean') {
    setFocusModeToStorage(state.focusMode.isFocusMode);
  }

  const mode = state.focusMode?.colorMode;
  if (mode === 'light' || mode === 'dark' || mode === 'system') {
    setColorModeToStorage(mode);
  }
}

export function storeRouterStateToLocalStorage(path: string) {
  optimizedStorage.set('router', path, 50); // 50msデバウンス
}

export function storeRirekishoPathToLocalStorage(path: string) {
  optimizedStorage.set('rirekisho_current_path', path, 50); // 50msデバウンス
}

export function getReduxStoreFromLocalStorage(): RootState | null {
  return optimizedStorage.get('app', null);
}

export function getRouterStateFromLocalStorage(): string {
  return optimizedStorage.get('router', '');
}

export function deleteAllStorageValue() {
  // 最適化ストレージのキーを削除
  optimizedStorage.remove('app');
  optimizedStorage.remove('router');

  // フォーカスモードを削除
  clearFocusModeFromStorage();
  clearColorModeFromStorage();

  // タイポチェック設定を削除
  clearTypoCheckFromStorage();

  // モックモードを削除
  clearMockModeFromStorage();

  // 履歴書の現在のパスを削除
  try {
    localStorage.removeItem('rirekisho_current_path');
  } catch {
    // エラーは無視
  }

  // キャッシュをクリア
  clearStorageCache();
}

/**
 * モックモードの状態をローカルストレージで管理（最適化版）
 */
const MOCK_MODE_KEY = 'resume_commit_mock_mode';

/**
 * モックモードの状態を取得（最適化版）
 */
export function getMockModeFromStorage(): boolean | null {
  // キャッシュを完全に無効化して、常にローカルストレージから最新の値を取得
  try {
    const stored = localStorage.getItem(MOCK_MODE_KEY);
    if (stored === null) {
      return null;
    }
    const parsed = JSON.parse(stored);
    return typeof parsed === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * モックモードの状態を保存（最適化版）
 */
export function setMockModeToStorage(isMockMode: boolean): void {
  // キャッシュを完全に無効化して、ローカルストレージに即座に保存
  try {
    localStorage.setItem(MOCK_MODE_KEY, JSON.stringify(isMockMode));
  } catch {
    // エラーは無視（localStorage が利用できない環境など）
  }
}

/**
 * モックモードの状態をクリア（最適化版）
 */
function clearMockModeFromStorage(): void {
  try {
    localStorage.removeItem(MOCK_MODE_KEY);
  } catch {
    // エラーは無視（localStorage が利用できない環境など）
  }
}

/**
 * フォーカスモードの状態をローカルストレージで管理（最適化版）
 */
const FOCUS_MODE_KEY = 'resume_commit_focus_mode';

/**
 * フォーカスモードの状態を取得（最適化版）
 */
export function getFocusModeFromStorage(): boolean {
  return optimizedStorage.get(FOCUS_MODE_KEY, false);
}

/**
 * フォーカスモードの状態を保存（最適化版）
 */
export function setFocusModeToStorage(isFocusMode: boolean): void {
  optimizedStorage.set(FOCUS_MODE_KEY, isFocusMode, 100);
}

/**
 * フォーカスモードの状態をクリア（最適化版）
 */
function clearFocusModeFromStorage(): void {
  optimizedStorage.remove(FOCUS_MODE_KEY);
}

/**
 * カラーモードの状態をローカルストレージで管理
 */
const COLOR_MODE_KEY = 'resume_commit_color_mode';

type StoredColorMode = 'light' | 'dark' | 'system';

export function getColorModeFromStorage(): StoredColorMode | null {
  try {
    const stored = localStorage.getItem(COLOR_MODE_KEY);
    if (stored === null) {
      return null;
    }
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
}

export function setColorModeToStorage(mode: StoredColorMode): void {
  optimizedStorage.set(COLOR_MODE_KEY, mode, 100);
}

function clearColorModeFromStorage(): void {
  try {
    localStorage.removeItem(COLOR_MODE_KEY);
  } catch {
    // エラーは無視
  }
}

const TYPO_CHECK_KEY = 'resume_commit_typo_check_enabled';

export function getTypoCheckFromStorage(): boolean {
  return optimizedStorage.get(TYPO_CHECK_KEY, true);
}

export function setTypoCheckToStorage(isEnabled: boolean): void {
  optimizedStorage.set(TYPO_CHECK_KEY, isEnabled, 100);
}

export function clearTypoCheckFromStorage(): void {
  optimizedStorage.remove(TYPO_CHECK_KEY);
}

/**
 * ローカルストレージのキャッシュをクリア
 */
function clearStorageCache(): void {
  optimizedStorage.clearCache();
}
